// Copyright (c) 2019-2020 Verimatrix, Inc.  All Rights Reserved.

// @flow
import axios from 'axios'
import {APIUrl, APIHeaders} from './utils'
import {UAI_API} from '../config'
import {stopSubmit, setSubmitSucceeded, setSubmitFailed, startSubmit} from 'redux-form'
import jwt_decode from 'jwt-decode'
import history from '../history'

import {errorToast, successToast} from './toastMessages'
import {showAreYouThere} from './areYouThere'
import {showSpinner} from './spinner'

export const USER_LOGIN = 'user_login'
export const USER_LOGOUT = 'user_logout'
export const FETCH_USER = 'fetch_user'
export const APP_INITIALIZED = 'app_initialized'
export const CHANGE_PASSWORD = 'change_password'
export const RESET_PASSWORD = 'reset_password'
export const FORGOT_PASSWORD = 'forgot_password'
export const ACTIVATE_USER = 'activate_user'
export const USER_ACTIVATED = 'user_activated'

export const LOCAL_STORAGE_TOKEN = 'token'

// TODO: Remove this after Refresh Token implementation
export const LOCAL_STORAGE_PASSWORD = 'password'

const USER_NOT_ACTIVATED_CODE = 'AUTH-325'

const REFERSH_TOKEN_TIME = 5

export function fetchMyUser () {
  return (dispatch, getState) => {
    const {session} = getState()
    const url = APIUrl('user/me/', {}, UAI_API)
    axios.get(url, {headers: APIHeaders(session)}).then(data => {
      dispatch({
        type: FETCH_USER,
        payload: data.data
      })
    })
  }
}

export function login (values, showSpinnerModal = true) {
  return (dispatch) => {
    if (showSpinnerModal) {
      dispatch(showSpinner(true))
    }

    return axios.post(APIUrl('auth/login', {}, UAI_API), values).then(data => {
      if (showSpinnerModal) {
        dispatch(showSpinner(false))
      }

      const token = data.data
      const user_data = jwt_decode(token)
      window.localStorage.setItem(LOCAL_STORAGE_TOKEN, token)

      // TODO: Remove this after Refresh Token implementation
      const password = btoa(values.password)
      window.localStorage.setItem(LOCAL_STORAGE_PASSWORD, password)

      dispatch({
        type: USER_LOGIN,
        payload: {
          token: token,
          user_data: user_data,
        }
      })
    }).catch( request => {
      if (showSpinnerModal) {
        dispatch(showSpinner(false))
      }

      if (!request.response) {
        dispatch(errorToast('Connection error, please try again later.'))
        dispatch(stopSubmit('login'))
      } else {

        let {code} = request.response.data
        let {username} = values
        let {password} = values

        if (code === USER_NOT_ACTIVATED_CODE) {
          dispatch({
            type: ACTIVATE_USER,
            payload: {
              username: username,
              password: password
            }
          })
          history.push(`/activate-user/`)
        } else {
          dispatch(stopSubmit('login',
            {_error: 'Incorrect username or password'})
          )
        }
      }
    })
  }
}

export function changePassword (values) {
  return (dispatch, getState) => {
    const {session} = getState()
    const updated_values = {
      username: session.user_data.email,
      password: values.password,
      newPassword: values.newPassword
    }
    dispatch(startSubmit('changePassword'))
    dispatch(showSpinner(true))
    return axios.put(APIUrl('auth/changePassword', {}, UAI_API), updated_values, {headers: APIHeaders(session)}).then(() => {
      dispatch(showSpinner(false))
      dispatch({
        type: CHANGE_PASSWORD
      })
      dispatch(stopSubmit('changePassword'))
      dispatch(setSubmitSucceeded('changePassword'))
      dispatch(successToast('Your password has been changed successfully.'))

      // TODO: Remove this after Refresh Token implementation
      const password = btoa(values.newPassword)
      window.localStorage.setItem(LOCAL_STORAGE_PASSWORD, password)

    }).catch( () => {
      dispatch(setSubmitFailed('changePassword'))
      dispatch(showSpinner(false))
      dispatch(stopSubmit('changePassword',
        {password: 'Current password is incorrect'})
      )
    })
  }
}

export function forgotPassword (values) {
  return (dispatch) => {
    dispatch(startSubmit('resetPassword'))
    dispatch(showSpinner(true))
    return axios.post(APIUrl('auth/forgotPassword', {}, UAI_API), values, {headers: {'Content-Type': 'application/json'}}).then(() => {
      dispatch({
        type: FORGOT_PASSWORD,
        payload: {email: values.username}
      })
      dispatch(stopSubmit('resetPassword'))
      dispatch(setSubmitSucceeded('resetPassword'))
      dispatch(showSpinner(false))
    }).catch( () => {
      dispatch(setSubmitFailed('resetPassword'))
      dispatch(stopSubmit('resetPassword',
        {username: 'Current email is incorrect'})
      )
      dispatch(showSpinner(false))
    })
  }
}

export function resetPassword (values) {
  return (dispatch, getState) => {
    const {resetPassword} = getState()
    const updated_values = {
      username: resetPassword.email,
      token: values.token,
      newPassword: values.newPassword
    }
    dispatch(startSubmit('resetPassword'))
    dispatch(showSpinner(true))
    return axios.put(APIUrl('auth/resetPassword', {}, UAI_API), updated_values, {}).then(() => {
      const login_values = {
        username: updated_values.username,
        password: values.newPassword
      }

      dispatch(showSpinner(false))
      dispatch({
        type: RESET_PASSWORD
      })
      dispatch(stopSubmit('resetPassword'))
      dispatch(setSubmitSucceeded('resetPassword'))
      dispatch(login(login_values))
      history.push('/')
      dispatch(successToast('Your password has been changed successfully.'))
    }).catch( () => {
      dispatch(setSubmitFailed('resetPassword'))
      dispatch(showSpinner(false))
      dispatch(stopSubmit('resetPassword',
        {temporaryPassword: 'Temporary password is incorrect'})
      )
    })
  }
}

export function activateUser (values) {
  return (dispatch, getState) => {
    const {activateUser} = getState()

    const {username, password} = activateUser

    const updated_values = {
      username: username,
      password: password,
      newPassword: values.newPassword
    }
    return axios.put(APIUrl('auth/activate', {}, UAI_API), updated_values, {headers: {'Content-Type': 'application/json'}}).then(() => {
      const login_values = {
        username: username,
        password: values.newPassword
      }

      dispatch({
        type: USER_ACTIVATED
      })

      dispatch(login(login_values))
      history.push('/')
    }).catch( () => {
      dispatch(stopSubmit('activateUser', {_error: 'Error while updating password'})
      )
    })
  }
}

export function logout () {
  return (dispatch, getState) => {

    const {areYouThere} = getState()

    if (areYouThere) {
      const {show} = getState().areYouThere

      if (show) {
        dispatch(showAreYouThere(false))
      }
    }

    window.localStorage.removeItem(LOCAL_STORAGE_TOKEN)
    window.localStorage.removeItem(LOCAL_STORAGE_PASSWORD)

    dispatch({
      type: USER_LOGOUT
    })
  }
}

export function resumeSession () {
  return (dispatch) => {
    const token = window.localStorage.getItem(LOCAL_STORAGE_TOKEN)
    if (token) {
      const user_data = jwt_decode(token)
      const tokenExpDate = user_data.exp * 1000
      const now = new Date().getTime()

      if (now >= tokenExpDate) {
        dispatch(logout)
      } else {
        dispatch({
          type: USER_LOGIN,
          payload: {
            token: token,
            user_data: user_data
          }
        })
      }
    }
  }
}

export function verifySessionToken () {
  return (dispatch, getState) => {
    const {user_data} = getState().session
    if (user_data) {
      const tokenExpDate = getState().session.user_data.exp * 1000

      const date_now = new Date()
      const expiracy_date_check = (new Date().setMinutes(date_now.getMinutes() + REFERSH_TOKEN_TIME))
      const refreshTokenTime = (new Date(expiracy_date_check)).getTime()

      if (refreshTokenTime >= tokenExpDate) {
        dispatch(loginWithoutPassword())
      }
    }
  }
}

export function loginWithoutPassword () {
  return (dispatch) => {
    const token = window.localStorage.getItem(LOCAL_STORAGE_TOKEN)
    const passwordBase64 = window.localStorage.getItem(LOCAL_STORAGE_PASSWORD)

    const userData = jwt_decode(token)
    let userName = userData['cognito:username']
    let password = atob(passwordBase64)

    let values = {
      username: userName,
      password: password
    }

    dispatch(login(values, false))
  }
}
